import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ResultRow } from '../../reporting/arrowToRows';
import {
  axisCharBudget,
  axisIdForKind,
  axisTicks,
  capCategories,
  CATEGORY_SEP,
  type ChartSeries,
  compactTick,
  countAxisIsIntegerOnly,
  horizontalChartHeight,
  MAX_CATEGORIES,
  preferHorizontalBars,
  resolveSeries,
  seriesUnits,
  sharedLeadingDimensions,
  truncateLabel,
  valueAxisMax,
  wrapLabelLines,
} from '../../reporting/chartFormat';
import {
  type DimensionFormat,
  formatValue,
  humanizeHeader,
  resolveFormatKind,
} from '../../reporting/formatValue';
import { BarChart3, Info } from 'lucide-react';
import type { DatasetColumn } from '../../types/reporting';

const AXIS_TICK = 'hsl(var(--muted-foreground))';
const GRID_STROKE = 'hsl(var(--border))';
// Hover band behind the active category. The old rgba(0,0,0,0.04) was invisible
// on the dark surface; a theme token stays visible-but-recessive in both modes.
const CURSOR_FILL = 'hsl(var(--muted))';
// Width of the horizontal-bar category (Y) axis band. Wide enough to show full
// payer names wrapped onto two lines instead of clipping "Traditional Medicar…";
// kept under ~180 so the bars still get a readable plot area, and reused as the
// fixed value-ruler's left inset so its ticks stay aligned with the bars.
const H_CATEGORY_AXIS_WIDTH = 176;
// A narrow card gives the names less room and the scale three labels, so the
// bars and the scale's labels still fit in a 300px card.
const NARROW_CATEGORY_AXIS_WIDTH = 120;
const NARROW_TICK_STEPS = 2;
// Ring drawn on the hovered bar (activeBar) so the mark lifts on hover — a
// surface-colored hairline separates the bar from its neighbours.
const ACTIVE_BAR_STROKE = 'hsl(var(--background))';
// Synthetic per-row field holding the composite category label (all group-by
// dimensions joined); the X axis and tooltip key off this instead of a single
// dimension column. Exported so the tooltip test can key a hovered row off it.
export const CATEGORY_KEY = '__category';

interface TooltipItem {
  dataKey?: string | number;
  value?: number;
  color?: string;
  name?: string;
  // The full data row recharts hands each tooltip item; the category label lives
  // on it under CATEGORY_KEY. Read from here (not recharts' ambiguous `label`,
  // which binds to the value axis in the horizontal layout and prints a stray 0).
  payload?: Record<string, unknown>;
}

/**
 * Compact, fast hover card: the bar's category label plus its pretty value(s).
 *  - Title is the hovered bar's category (CATEGORY_KEY off the data row), so it
 *    reads "Traditional Medicare" in BOTH orientations — recharts' `label` is
 *    unreliable horizontally (layout='vertical' resolves it to the value axis and
 *    prints "0"); `?? label` keeps the direct unit-test path working.
 *  - Single series: one baseline-aligned row (title left, value right) — no
 *    swatch, no series name, no per-dimension breakdown to decode.
 *  - Multiple series: the title on its own line, then one light row per series
 *    (swatch / muted name / right-aligned value).
 * Values use formatValue (exact, not compact — compact belongs on axes) off the
 * already-built kindByKey map, so nothing heavy runs per hover. Hoisted/exported
 * so the value/currency wiring is unit-testable without a live hover (jsdom can't
 * drive one).
 */
export const ResultsChartTooltip = ({
  active,
  label,
  payload,
  kindByKey,
}: {
  active?: boolean;
  label?: unknown;
  payload?: TooltipItem[];
  kindByKey: Map<string, ChartSeries['kind']>;
}) => {
  if (!active || !payload?.length) return null;
  const title = String(payload[0]?.payload?.[CATEGORY_KEY] ?? label ?? '');
  const valueFor = (item: TooltipItem): string =>
    formatValue(
      item.value,
      kindByKey.get(String(item.dataKey ?? '')) ?? 'number'
    );
  return (
    <div className='w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-popover px-3 py-2 text-sm shadow-lg'>
      {payload.length === 1 ? (
        <div className='flex items-baseline justify-between gap-3'>
          <span className='font-medium text-foreground'>{title}</span>
          <span className='text-right font-medium tabular-nums text-foreground'>
            {valueFor(payload[0])}
          </span>
        </div>
      ) : (
        <>
          <p className='mb-1.5 border-b border-border pb-1.5 font-medium text-foreground'>
            {title}
          </p>
          <ul className='grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1'>
            {payload.map(item => (
              <li
                key={String(item.dataKey ?? '')}
                className='col-span-3 grid grid-cols-subgrid items-center'
              >
                <span
                  className='h-2.5 w-2.5 shrink-0 rounded-sm'
                  style={{ backgroundColor: item.color }}
                />
                <span className='truncate text-muted-foreground'>
                  {item.name}
                </span>
                <span className='text-right font-medium tabular-nums text-foreground'>
                  {valueFor(item)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

/**
 * Centered placeholder shown when there's nothing to chart (no measure yet) or
 * when the data can't be charted safely (too many categories). A lucide mark
 * plus muted copy frames the state instead of a bare sentence. The `message`
 * wording is passed through verbatim so the copy-contract stays intact.
 */
const EmptyChartState = ({ message }: { message: string }) => (
  <div className='flex h-96 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-center'>
    <BarChart3 className='h-8 w-8 text-muted-foreground/60' aria-hidden />
    <p className='max-w-xs text-sm text-muted-foreground'>{message}</p>
  </div>
);

interface ResultsChartProps {
  columns: string[];
  rows: ResultRow[];
  dimensionKeys: string[];
  columnLabels?: Map<string, string>;
  columnMeta?: Map<string, DatasetColumn>;
  dimFormats?: Map<string, DimensionFormat>;
  // Pivot column name (when the report pivots), so pivot value series names map
  // through the column's display formatter (claim_type → "Medicare Part A").
  pivotColumn?: string;
  // The chart sits in a narrow card: always put category names on the Y axis so
  // they stay upright and cannot overlap.
  narrow?: boolean;
}

const ResultsChart = ({
  columns,
  rows,
  dimensionKeys,
  columnLabels,
  columnMeta,
  dimFormats,
  pivotColumn,
  narrow = false,
}: ResultsChartProps) => {
  const seriesKeys = columns.filter(c => !dimensionKeys.includes(c));
  if (dimensionKeys.length === 0 || seriesKeys.length === 0) {
    return (
      <EmptyChartState message='Add a group by and a measure to chart this report.' />
    );
  }

  // Format every group-by dimension independently; the composite X category is
  // these joined. Kept as a per-dimension array (not just the joined string) so
  // shared-prefix detection, stripping, and the surfaced context all compare real
  // dimension values instead of splitting the composite label — a value can
  // itself contain CATEGORY_SEP. Each dimension keeps its own kind / date
  // granularity; null → "None" (fixes the raw "—" tick for missing values).
  const dimFormatsResolved = dimensionKeys.map(key => ({
    key,
    kind: resolveFormatKind(key, columnMeta, dimFormats?.get(key)),
    granularity: dimFormats?.get(key)?.dateGranularity,
  }));
  // A date axis is a single dimension whose resolved kind is 'date' — an exact
  // date OR a date_trunc week/month/quarter (resolveFormatKind returns 'date' for
  // both). Time is the meaning of the axis, so it must stay chronological (never
  // re-ranked), always vertical (time on X, not a sideways date list), and gets
  // the higher date-category ceiling. See capCategories / preferHorizontalBars.
  const dateAxis =
    dimensionKeys.length === 1 && dimFormatsResolved[0].kind === 'date';
  const dimValues = (row: ResultRow): string[] =>
    dimFormatsResolved.map(({ key, kind, granularity }) =>
      formatValue(row[key], kind, {
        dateGranularity: granularity,
        emptyText: 'None',
      })
    );

  const {
    series,
    rows: seriesRows,
    hiddenSeriesCount,
  } = resolveSeries(rows, seriesKeys, columnLabels, columnMeta, pivotColumn);

  // Cardinality ceiling: cap the rendered categories so a huge group-by (e.g.
  // one bar per patient) can't freeze the browser. A rankable measure set
  // (additive and a single measure alias) keeps the top-N by total; a
  // non-additive set or one mixing distinct measures (dollars + counts, or
  // claims + days) can't be ranked meaningfully, so we skip the chart entirely
  // and point at the table. Cap before formatting labels so we only format the
  // kept rows.
  const {
    rows: cappedRows,
    hiddenCategoryCount,
    overflow,
  } = capCategories(seriesRows, seriesKeys, MAX_CATEGORIES, dateAxis);
  if (overflow) {
    return (
      <EmptyChartState
        message={
          dateAxis
            ? 'Too many days to chart — group by week or month, or shorten the date window.'
            : 'Too many categories to chart — filter or use table view.'
        }
      />
    );
  }

  // Per-dimension formatted values for each kept row, plus the composite label
  // the X axis and tooltip key off. Computed once so labels, shared-prefix
  // detection, and the surfaced context all agree.
  const perRowDims = cappedRows.map(dimValues);
  const chartRows = cappedRows.map((row, i) => ({
    ...row,
    [CATEGORY_KEY]: perRowDims[i].join(CATEGORY_SEP),
  }));
  const { hasMoney, hasCount } = seriesUnits(series);
  // Force integer ticks only when the count axis carries nothing but whole-row
  // count measures. A non-money average/min/max or a sum of a non-money column
  // can be fractional, so it keeps a decimal scale (allowDecimals stays true).
  const countAxisInteger = countAxisIsIntegerOnly(series);
  const kindByKey = new Map(series.map(s => [s.key, s.kind]));

  // Drop the leading group-by dimensions shared by every category (e.g. "0-30 · "
  // when all rows share one bucket) so the distinguishing text isn't truncated
  // off the tick. Shared-ness and stripping work on real dimension values, never
  // by splitting the composite label. The full label survives in the tooltip.
  const sharedDims = sharedLeadingDimensions(perRowDims);
  const strippedByLabel = new Map(
    perRowDims.map(dims => [
      dims.join(CATEGORY_SEP),
      dims.slice(sharedDims).join(CATEGORY_SEP),
    ])
  );
  const displayLabel = (value: string): string =>
    strippedByLabel.get(value) ?? value;

  // The shared prefix is stripped off every tick to save room; surface it once
  // above the plot as "Dimension: value" (e.g. "Days outstanding: 0-30") so the
  // context isn't buried in the hover. Each value comes straight from the first
  // displayed row's dimension — not from re-splitting the composite label — so a
  // value containing CATEGORY_SEP can't corrupt the label-to-dimension mapping.
  const firstDims = perRowDims[0] ?? [];
  const sharedContext = dimFormatsResolved
    .slice(0, sharedDims)
    .map(
      ({ key }, i) =>
        `${humanizeHeader(key, columnLabels, columnMeta)}: ${firstDims[i]}`
    );

  // Rotate labels only when the categories themselves are dense enough to
  // collide: horizontal with a small count (label length is handled by wrapping,
  // not rotation), toward vertical as categories get denser (a vertical label
  // occupies ~one font-width, so it can't overlap its neighbours), and thin the
  // rendered ticks when even vertical labels wouldn't fit — the full value stays
  // in the tooltip.
  const categoryCount = chartRows.length;
  // Longest displayed (shared-prefix-stripped) category label; drives the
  // orientation decision so several long labels flip the chart horizontal
  // instead of cramping/rotating vertical ticks.
  const maxLabelChars = chartRows.reduce(
    (max, row) => Math.max(max, displayLabel(String(row[CATEGORY_KEY])).length),
    0
  );
  // Horizontal (categories on the Y axis, fully upright) for the readability
  // cases rotation can't fix — many categories or several long labels; vertical
  // otherwise (few short categories, where bars are wide and labels wrap). A date
  // axis is the one exception: it stays vertical (time on X) no matter how many
  // days, because a horizontal flip would render the timeline as a sideways date
  // list. The pure preferHorizontalBars is unchanged — the exception lives here.
  // The horizontal value ruler starts at zero, so a narrow chart with a
  // negative value keeps the vertical layout.
  const hasNegative = cappedRows.some(row =>
    series.some(s => {
      const value = row[s.key];
      return typeof value === 'number' && value < 0;
    })
  );
  const horizontal =
    !dateAxis &&
    ((narrow && !hasNegative) ||
      preferHorizontalBars(categoryCount, maxLabelChars));
  const angle = categoryCount > 16 ? -90 : categoryCount > 8 ? -45 : 0;
  const angled = angle !== 0;
  const vertical = angle === -90;
  // Horizontal ticks wrap onto multiple lines (each composite dimension / word
  // group on its own line) instead of clipping distinct long labels to the same
  // stub. Fewer categories → wider per-bar width → more chars/line and more
  // lines before truncating.
  const charBudget = axisCharBudget(categoryCount);
  const maxLines = categoryCount <= 4 ? 3 : 2;
  // Cap how many labels render so rotated text can't collide; recharts' `interval`
  // is "render 1, skip N", so translate a max-visible count into that stride.
  const maxTicks = vertical ? 30 : angled ? 18 : 12;
  const tickInterval =
    categoryCount <= maxTicks ? 0 : Math.ceil(categoryCount / maxTicks) - 1;
  const axisHeight = vertical ? 128 : angled ? 76 : maxLines * 16 + 8;
  // End-truncate rotated labels to what actually fits inside the axis band so the
  // SVG never front-clips the start of a name ("rian Erickson", "assandra
  // Willi…"). A -90 label runs straight down the band height; a -45 label runs
  // along the longer diagonal. Budget a conservative ~8px per glyph (wide capital
  // names run wider than the 6-7px average) so worst-case labels still fit; the
  // tooltip always carries the full value.
  const rotatedRun = vertical
    ? axisHeight - 16
    : (axisHeight - 12) * Math.SQRT2;
  const rotatedMaxChars = Math.max(6, Math.floor(rotatedRun / 8));

  const CategoryTick = ({
    x,
    y,
    payload,
  }: {
    x?: number;
    y?: number;
    payload?: { value?: unknown };
  }) => {
    const full = displayLabel(String(payload?.value ?? ''));
    // Rotated: one line along the diagonal (more room than a wrapped column);
    // horizontal: wrap into stacked lines so long distinct labels stay distinct.
    if (angled) {
      return (
        <text
          x={x}
          y={y}
          dy={4}
          dx={vertical ? -4 : 0}
          textAnchor='end'
          transform={`rotate(${angle}, ${x}, ${y})`}
          fontSize={12}
          fill={AXIS_TICK}
        >
          {truncateLabel(full, rotatedMaxChars)}
        </text>
      );
    }
    const lines = wrapLabelLines(full, charBudget, maxLines);
    return (
      <text x={x} y={y} textAnchor='middle' fontSize={12} fill={AXIS_TICK}>
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 12 : 14}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  // Horizontal category (Y) axis tick: renders the full category name wrapped
  // onto up to two lines within the axis band instead of hard-truncating to 20
  // chars, so long payer names ("Traditional Medicare Advantage") stay readable.
  // Right-aligned (textAnchor='end') against the band edge; the tspans are
  // vertically centered on the tick's y so a 1- or 2-line label sits on the bar.
  const H_LINE_HEIGHT = 13;
  // ~7px/glyph across the band (minus a small right gutter) → ~24 chars/line.
  const categoryAxisWidth = narrow
    ? NARROW_CATEGORY_AXIS_WIDTH
    : H_CATEGORY_AXIS_WIDTH;
  const hCategoryBudget = Math.floor((categoryAxisWidth - 8) / 7);
  const HCategoryTick = ({
    x,
    y,
    payload,
  }: {
    x?: number;
    y?: number;
    payload?: { value?: unknown };
  }) => {
    const full = displayLabel(String(payload?.value ?? ''));
    const lines = wrapLabelLines(full, hCategoryBudget, 2);
    return (
      <text x={x} y={y} textAnchor='end' fontSize={12} fill={AXIS_TICK}>
        {lines.map((line, i) => (
          <tspan
            key={i}
            x={x}
            dy={
              i === 0
                ? -((lines.length - 1) * H_LINE_HEIGHT) / 2 + 4
                : H_LINE_HEIGHT
            }
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  // Money and counts live on separate axes so a small-scaled count series
  // isn't crushed to an invisible sliver against a large money range, and each
  // axis formats its own ticks ($ vs plain). When both units are present the
  // money axis moves to the second side; a single-unit chart keeps one axis.
  const mixed = hasMoney && hasCount;
  // In a dual-axis chart, tie each series to the axis that scales it so the
  // legend (not just a hover) says which measure reads off which side. The side
  // words track orientation: vertical → count left / money right; horizontal →
  // count bottom / money top (where the value axes actually live).
  const axisSuffix = (key: string): string => {
    if (!mixed) return '';
    const isMoney = axisIdForKind(kindByKey.get(key) ?? 'number') === 'money';
    if (horizontal) return isMoney ? ' (top)' : ' (bottom)';
    return isMoney ? ' (right)' : ' (left)';
  };

  // Axis titles (recharts' built-in `label`, no custom SVG). The value-axis
  // title only shows when there's a single series (no legend to duplicate); the
  // category-axis title only shows in the vertical orientation for a single
  // remaining dimension (horizontal already labels each category on the Y axis).
  const axisTitleStyle = { fill: AXIS_TICK, fontSize: 12 };
  const valueAxisTitle = series.length === 1 ? series[0].name : undefined;
  const remainingDims = dimensionKeys.slice(sharedDims);
  const categoryAxisTitle =
    remainingDims.length === 1
      ? humanizeHeader(dimensionKeys[sharedDims], columnLabels, columnMeta)
      : undefined;

  // Tooltip, legend, and bars are identical across orientations, so build them
  // once. Bars bind to whichever axis pair the orientation uses (vertical: value
  // on the Y axis by kind; horizontal: value on the X axis by kind, category on
  // the shared 'cat' Y axis) — a Bar missing a valid axis id is silently dropped.
  const tooltipEl = (
    <Tooltip
      // Recharts v3 picks the tooltip axis TYPE from the layout but defaults
      // its ID to 0. The horizontal chart's category axis is yAxisId='cat', so
      // without this the hover hit-testing binds to an implicit numeric Y axis
      // and resolves a handful of huge bands instead of one per bar (hovering
      // bar 7 showed bar 2's tooltip). Vertical bars keep the default: their
      // categorical XAxis already has ID 0.
      axisId={horizontal ? 'cat' : 0}
      shared
      cursor={{ fill: CURSOR_FILL, fillOpacity: 0.4 }}
      content={<ResultsChartTooltip kindByKey={kindByKey} />}
    />
  );
  const legendEl = series.length > 1 && (
    <Legend
      // Render above the plot so the axis-ownership key stays visible even at
      // short viewports, where a bottom legend falls below the fold. Rectangular
      // swatches mirror the bar marks (a dot wouldn't match the bar geometry).
      verticalAlign='top'
      align='center'
      iconType='rect'
      iconSize={10}
      wrapperStyle={{ paddingBottom: 12 }}
      formatter={(value: string, entry?: { dataKey?: unknown }) => {
        const suffix = axisSuffix(String(entry?.dataKey ?? ''));
        return (
          <span className='text-sm text-foreground'>
            {value}
            {suffix && <span className='text-muted-foreground'>{suffix}</span>}
          </span>
        );
      }}
    />
  );
  const barEls = series.map((s: ChartSeries) => (
    <Bar
      key={s.key}
      {...(horizontal
        ? { xAxisId: axisIdForKind(s.kind), yAxisId: 'cat' }
        : { yAxisId: axisIdForKind(s.kind) })}
      dataKey={s.key}
      name={s.name}
      fill={s.color}
      maxBarSize={40}
      // Round the growing end of the bar (top for vertical, right for horizontal).
      radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
      isAnimationActive={false}
      // Hovered bar lifts: full opacity plus a thin surface ring so it reads as
      // active against the cursor band. Cheap (no per-Cell state).
      activeBar={{
        fillOpacity: 1,
        stroke: ACTIVE_BAR_STROKE,
        strokeWidth: 1,
      }}
    />
  ));

  const disclosures = (
    <>
      {sharedContext.length > 0 && (
        <p className='text-xs text-muted-foreground'>
          {sharedContext.join(CATEGORY_SEP)}
        </p>
      )}
      {hiddenCategoryCount > 0 && (
        <p className='inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground'>
          <Info className='h-3.5 w-3.5 shrink-0' aria-hidden />
          Showing top {MAX_CATEGORIES} of {MAX_CATEGORIES + hiddenCategoryCount}{' '}
          categories — switch to table view to see all values.
        </p>
      )}
      {hiddenSeriesCount > 0 && (
        <p className='inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground'>
          <Info className='h-3.5 w-3.5 shrink-0' aria-hidden />
          Showing {series.length} of {series.length + hiddenSeriesCount} series
          — switch to table view to see all values.
        </p>
      )}
    </>
  );

  // Horizontal orientation: category names on the Y axis (fully upright, no
  // rotation/front-clipping), value axes on the X. The plot grows with the row
  // count and scrolls so 50 capped categories stay individually readable — but
  // the value scale and legend can't ride inside that scroll area (they'd scroll
  // off and leave mid-list bars with no reference, and a mixed money+count chart
  // could never show both scales together). They live instead in a FIXED HTML
  // header above the scroll region. The header ruler and the scrolling plot share
  // ONE explicit value domain, and the ruler is inset by the same left offset as
  // the plot (150px category axis + 8px margin), so its ticks stay aligned with
  // the bars as they scroll. HTML (not a second recharts axis) because recharts
  // renders neither ticks nor a legend for a hidden-only series.
  if (horizontal) {
    const countMax = valueAxisMax(chartRows, series, 'count');
    const moneyMax = valueAxisMax(chartRows, series, 'money');
    const tickSteps = narrow ? NARROW_TICK_STEPS : undefined;
    const countTicks = axisTicks(countMax, tickSteps);
    const moneyTicks = axisTicks(moneyMax, tickSteps);
    // Left inset = plot margin (8) + category axis width; right inset = plot
    // margin (16). justify-between then spreads the ticks across exactly the
    // bars' horizontal extent (first at the origin, last at the max bar's end).
    const rulerRow = (
      ticks: number[],
      money: boolean,
      key: string
    ): JSX.Element => (
      <div
        key={key}
        className='flex justify-between text-xs tabular-nums text-muted-foreground'
        style={{ paddingLeft: categoryAxisWidth + 8, paddingRight: 16 }}
      >
        {ticks.map((tick, i) => (
          <span key={i}>{compactTick(tick, money)}</span>
        ))}
      </div>
    );
    // In a narrow card the chart shrinks to the card's height and its bars
    // scroll under the scale; on phones the bars stop at 16rem.
    return (
      <div className={narrow ? 'flex min-h-0 flex-col gap-2' : 'space-y-2'}>
        {disclosures}
        <div
          className={
            narrow
              ? 'flex min-h-0 flex-col rounded-lg border border-border'
              : 'rounded-lg border border-border'
          }
        >
          {/* Fixed header: legend + value scale(s), always visible above the plot. */}
          <div className='space-y-1 px-1 pt-2'>
            {series.length > 1 && (
              <div className='flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm'>
                {series.map(s => {
                  const suffix = axisSuffix(s.key);
                  return (
                    <span
                      key={s.key}
                      className='inline-flex items-center gap-1.5'
                    >
                      <span
                        className='h-2.5 w-2.5 rounded-sm'
                        style={{ backgroundColor: s.color }}
                      />
                      <span className='text-foreground'>{s.name}</span>
                      {suffix && (
                        <span className='text-muted-foreground'>{suffix}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
            {/* Single-series charts have no legend to name the measure, so the
                value-axis title stands in for it. */}
            {valueAxisTitle && (
              <p className='text-center text-xs text-muted-foreground'>
                {valueAxisTitle}
              </p>
            )}
            {/* Money ruler above the count ruler so the "(top)"/"(bottom)" legend
                suffixes stay truthful: money reads off the top ruler, count off
                the bottom. */}
            {hasMoney && rulerRow(moneyTicks, true, 'money')}
            {hasCount && rulerRow(countTicks, false, 'count')}
          </div>
          {/* Scrolling plot: bars + upright category names + gridlines. The value
              axes are present but hidden (the header shows the scale) and carry
              the header's explicit domain/ticks so the bars — and the gridlines —
              key off the same ruler. */}
          <div
            className={
              narrow
                ? 'min-h-0 w-full overflow-y-auto border-t border-border max-md:max-h-64'
                : 'max-h-[32rem] w-full overflow-y-auto border-t border-border'
            }
          >
            <div style={{ height: horizontalChartHeight(categoryCount) }}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                  layout='vertical'
                  data={chartRows}
                  margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                  barCategoryGap='20%'
                  barGap={2}
                >
                  <CartesianGrid
                    horizontal={false}
                    stroke={GRID_STROKE}
                    strokeOpacity={0.5}
                  />
                  <YAxis
                    yAxisId='cat'
                    type='category'
                    dataKey={CATEGORY_KEY}
                    width={categoryAxisWidth}
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                    tick={<HCategoryTick />}
                  />
                  {hasCount && (
                    <XAxis
                      xAxisId='count'
                      type='number'
                      domain={[0, countMax]}
                      ticks={countTicks}
                      allowDecimals={!countAxisInteger}
                      hide
                    />
                  )}
                  {hasMoney && (
                    <XAxis
                      xAxisId='money'
                      type='number'
                      domain={[0, moneyMax]}
                      ticks={moneyTicks}
                      orientation={mixed ? 'top' : 'bottom'}
                      hide
                    />
                  )}
                  {tooltipEl}
                  {barEls}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      {disclosures}
      <div className='h-96 w-full'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart
            data={chartRows}
            margin={{ top: 8, right: 16, bottom: angled ? 24 : 8, left: 8 }}
            barCategoryGap='20%'
            barGap={2}
          >
            <CartesianGrid
              vertical={false}
              stroke={GRID_STROKE}
              strokeOpacity={0.5}
            />
            <XAxis
              dataKey={CATEGORY_KEY}
              interval={tickInterval}
              height={axisHeight}
              axisLine={false}
              tickLine={false}
              tick={<CategoryTick />}
              label={
                categoryAxisTitle
                  ? {
                      value: categoryAxisTitle,
                      position: 'insideBottom',
                      offset: -4,
                      style: axisTitleStyle,
                    }
                  : undefined
              }
            />
            {hasCount && (
              <YAxis
                yAxisId='count'
                width={44}
                axisLine={false}
                tickLine={false}
                // Whole-row counts get integer-only ticks (no interpolated 0.5,
                // 1.5); a fractional-capable measure on this axis keeps decimals.
                allowDecimals={!countAxisInteger}
                tick={{ fontSize: 12, fill: AXIS_TICK }}
                tickFormatter={value => compactTick(value, false)}
                label={
                  valueAxisTitle
                    ? {
                        value: valueAxisTitle,
                        angle: -90,
                        position: 'insideLeft',
                        style: { ...axisTitleStyle, textAnchor: 'middle' },
                      }
                    : undefined
                }
              />
            )}
            {hasMoney && (
              <YAxis
                yAxisId='money'
                orientation={mixed ? 'right' : 'left'}
                width={56}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: AXIS_TICK }}
                tickFormatter={value => compactTick(value, true)}
                label={
                  valueAxisTitle
                    ? {
                        value: valueAxisTitle,
                        angle: -90,
                        position: mixed ? 'insideRight' : 'insideLeft',
                        style: { ...axisTitleStyle, textAnchor: 'middle' },
                      }
                    : undefined
                }
              />
            )}
            {tooltipEl}
            {legendEl}
            {barEls}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ResultsChart;
